import NextHead from "next/head";
import NextLink from "next/link";

import { useTranslator } from "@eo-locale/react";

import { useRouter } from "next/router";
import { useEffect, useState } from "react";

import Form from "./form";
import { useAppSelector } from "@/redux/store/hooks";
import {
  Text,
  Heading,
  Alert,
  AlertIcon,
  AlertTitle,
  CloseButton,
  AlertDescription,
  Flex,
  Button
} from "@/storybook-components";
import SocialSignIn from "./social-sign-in";
import SingleSignOn from "./single-sign-on";
import isRelativePath from "@/helpers/is-relative-path";
import { parseNextUrlFromQuery } from "@/helpers/parse-next-url";

const App = (): JSX.Element => {
  const router = useRouter();
  const { translate } = useTranslator();
  const { name } = useAppSelector((state) => state.site.site);
  const { disableSignup } = useAppSelector((state) => state.settings.settings);
  const { isLoggedIn } = useAppSelector((state) => state.user.registeredUser);
  const { features } = useAppSelector((state) => state.features);

  const isGranicusSSOEnabled = features?.granicusSso?.enabled;

  const [isPrivateRoute, setIsPrivateRoute] = useState(
    Boolean(router.query["private"]) === true
  );
  const [isPasswordReset, setIsPasswordReset] = useState(
    !!router.query["redirectionStatus"]
  );
  const [isSignUpDisabled, setIsSignUpDisabled] = useState(
    !!router.query["signupdisabled"]
  );

  // Parse and decode the next URL parameter
  const nextPageUrl = parseNextUrlFromQuery(router.query);

  // Redirect user after login
  useEffect(() => {
    if (isLoggedIn && process.env.NODE_ENV === "development") {
      if (nextPageUrl && isRelativePath(nextPageUrl)) router.push(nextPageUrl);
      else router.push("/");
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (isSignUpDisabled) {
      router.replace("/login", undefined, { shallow: true });
    }
  }, []);

  return (
    <>
      <NextHead>
        <title>{`${translate("login.signIn")} | ${name}`}</title>
        <meta name="description" content={translate("login.description")} />
      </NextHead>
      {isPrivateRoute && (
        <Alert status="info">
          <AlertIcon />
          <AlertTitle>{translate("login.thisPageIsPrivate")}</AlertTitle>
          <CloseButton ml="auto" onClick={() => setIsPrivateRoute(false)} />
        </Alert>
      )}
      {router.query["redirectionStatus"] && isPasswordReset && (
        <Alert status="success" alignItems="flex-start">
          <AlertIcon />
          <AlertTitle>{translate("login.passwordChanged")}</AlertTitle>
          <CloseButton ml="auto" onClick={() => setIsPasswordReset(false)} />
        </Alert>
      )}
      {isSignUpDisabled && (
        <Alert status="error" alignItems="flex-start">
          <AlertIcon />
          <Flex flexDirection="column">
            <AlertTitle>{translate("login.signUpDisabledTitle")}</AlertTitle>
            <AlertDescription>
              {translate("login.signUpDisabledDiscription")}
            </AlertDescription>
          </Flex>
          <CloseButton ml="auto" onClick={() => setIsSignUpDisabled(false)} />
        </Alert>
      )}
      <Heading as="h1" my={3}>
        {translate("login.signIn")}
      </Heading>
      <Form />

      {isGranicusSSOEnabled ? (
        <NextLink href="/auth/sso">
          <Button variant="secondary" my={3} size="lg" width="full">
            {translate("login.staffSignIn")}
          </Button>
        </NextLink>
      ) : (
        <SingleSignOn />
      )}

      {!disableSignup && (
        <>
          <SocialSignIn />
          <Text mt={4}>
            {translate("login.getInvolvedInConversations")}&nbsp;
            <NextLink
              href={
                nextPageUrl
                  ? `/register?next=${encodeURIComponent(nextPageUrl)}`
                  : "/register"
              }
              passHref
            >
              {translate("login.createAnAccount")}
            </NextLink>{" "}
            {translate("common.today")}
          </Text>
        </>
      )}
    </>
  );
};

export default App;
